import React, { useMemo } from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import {
  LearnMoreLink,
  MainWrapper,
  StyledGridItem,
  StyledIconWrapper,
  StyledMainGrid,
  MoreFeatures,
  MoreFeaturesLeft,
  MoreFeaturesRight,
  MoreFeatureWrapper,
  MoreFeatureTextContent,
  MoreFeatureDotWrapper,
  MoreFeatureDot,
  StyledAccordion,
  GridTitle,
  StyledHeader
} from './FeatureGrid.styled';
import Typography from '@latitude/typography';
import { REBRAND_COLOR } from '@latitude/core/utils/constants';
import { getCdrEntryBindingRichTextOptions } from '@/utils/contentfulRichTextBindings';
import styled from 'styled-components';

const COMPONENT_ID = 'feature-grid-v1';

const LINK_ITEM_CONTENT_TYPE = 'menuItems';

const getContentTypeId = entry =>
  entry?.contentType?.sys?.id ||
  entry?.contentType ||
  entry?.sys?.contentType?.sys?.id;

const getLinkHref = entry => entry?.link;

const getLinkLabel = entry => entry?.label || entry?.title;

const getTrackEventData = (entry, location, label) => ({
  category: 'text-link',
  action: 'internal-link',
  location: entry?.location || location,
  label: entry?.trackEventData || label
});

const hasChildren = children =>
  Array.isArray(children) ? children.some(Boolean) : Boolean(children);

const renderInlineLinkEntry = (
  node,
  children,
  location,
  componentIdentifier
) => {
  const target = node?.data?.target;
  const contentTypeId = getContentTypeId(target);

  if (contentTypeId !== LINK_ITEM_CONTENT_TYPE) return null;

  const href = getLinkHref(target);
  const label = getLinkLabel(target);
  const linkChildren = hasChildren(children) ? children : label;

  if (!href) return linkChildren || null;

  return (
    <LearnMoreLink
      noStyle
      className={`${componentIdentifier}-learn-more-link`}
      href={href}
      trackId={`${componentIdentifier}-learn-more`}
      trackEventData={getTrackEventData(target, location, label)}
    >
      {linkChildren}
    </LearnMoreLink>
  );
};

const getFeatureRichTextOptions = (location, componentIdentifier) =>
  getCdrEntryBindingRichTextOptions({
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_node, children) => <>{children}</>,
      [INLINES.HYPERLINK]: (node, children) => (
        <LearnMoreLink
          noStyle
          className={`${componentIdentifier}-learn-more-link`}
          href={node?.data?.uri}
          trackId={`${componentIdentifier}-learn-more`}
          trackEventData={{
            category: 'text-link',
            action: 'internal-link',
            location,
            label: children
          }}
        >
          {children}
        </LearnMoreLink>
      ),
      [INLINES.EMBEDDED_ENTRY]: (node, children) =>
        renderInlineLinkEntry(node, children, location, componentIdentifier)
    }
  });

const renderFeatureRichText = (feature, componentIdentifier) =>
  feature.richText
    ? documentToReactComponents(
        feature.richText,
        getFeatureRichTextOptions(
          feature.headingText || feature.title || 'feature-grid',
          componentIdentifier
        )
      )
    : feature.children || feature.content;

function splitArray(arr) {
  const n = arr.length;
  const mid = Math.ceil(n / 2); // left side gets extra if odd
  const left = arr.slice(0, mid);
  const right = arr.slice(mid);
  return [left, right];
}

/**
 * A grid component that displays a list of features with icons, headings, and descriptions.
 * Can also show additional features in an expandable accordion section.
 *
 * @param {Object} props - Component props
 * @param {Array<{id: string, title: string, content: string, productId: string}>} [props.features=[]] - Array of feature objects to display
 * @param {string} props.features[].id - Unique identifier for the feature
 * @param {string} props.features[].title - Title or heading for the feature
 * @param {string} props.features[].content - Description or content text for the feature
 * @param {string} props.features[].productId - Associated product identifier
 * @param {Array} [props.moreFeatures=[]] - Array of additional features to show in the accordion
 * @param {string} props.moreFeatures[].title - Title of the additional feature
 * @param {string} props.moreFeatures[].content - Content/description of the additional feature
 * @param {string} [props.moreFeatureTitle='View all benefits'] - Title for the expandable section
 * @param {string} [props.trackingId] - Optional tracking ID prefix for analytics
 * @param {Object} [rest] - Any additional props will be spread to the root element
 *
 * @returns {JSX.Element} The rendered FeatureGridV1 component
 */
const FeatureGridV1 = ({
  title,
  features = [],
  moreFeatures = [],
  moreFeatureTitle = 'View all benefits',
  trackingId,
  ...rest
}) => {
  const [moreLeft, moreRight] = useMemo(() => {
    return splitArray(moreFeatures);
  }, [moreFeatures]);

  const componentIdentifier = trackingId
    ? `${trackingId}-${COMPONENT_ID}`
    : `${COMPONENT_ID}`;

  const accordionData = [
    {
      id: 'view-all-benefits',
      title: moreFeatureTitle,
      content: (
        <>
          {moreFeatures && moreFeatures.length > 0 && (
            <MoreFeatures>
              {moreLeft &&
                moreLeft.map((feature, index) => (
                  <MoreFeaturesLeft key={`more-left-${index}`}>
                    <MoreFeatureWrapper>
                      <MoreFeatureDotWrapper>
                        <MoreFeatureDot />
                      </MoreFeatureDotWrapper>
                      <MoreFeatureTextContent>
                        <Typography.Body semibold color={REBRAND_COLOR.INDIGO}>
                          {feature.title}
                        </Typography.Body>
                        <Typography.Body small color={REBRAND_COLOR.INDIGO}>
                          {renderFeatureRichText(feature, componentIdentifier)}
                        </Typography.Body>
                      </MoreFeatureTextContent>
                    </MoreFeatureWrapper>
                  </MoreFeaturesLeft>
                ))}
              {moreRight &&
                moreRight.map((feature, index) => (
                  <MoreFeaturesRight key={`more-right-${index}`}>
                    <MoreFeatureWrapper>
                      <MoreFeatureDotWrapper>
                        <MoreFeatureDot />
                      </MoreFeatureDotWrapper>
                      <MoreFeatureTextContent>
                        <Typography.Body semibold color={REBRAND_COLOR.INDIGO}>
                          {feature.title}
                        </Typography.Body>
                        <Typography.Body small color={REBRAND_COLOR.INDIGO}>
                          {renderFeatureRichText(feature, componentIdentifier)}
                        </Typography.Body>
                      </MoreFeatureTextContent>
                    </MoreFeatureWrapper>
                  </MoreFeaturesRight>
                ))}
            </MoreFeatures>
          )}
        </>
      )
    }
  ];

  const RichTextWrapper = styled.div`
  a[class*='LearnMoreLink'] {
    color: ${REBRAND_COLOR.MID_BLUE} !important;
  }
`;

  return (
    <MainWrapper className={`${componentIdentifier}-wrapper`} {...rest}>
      {title && (
        <GridTitle className={`${componentIdentifier}-title`}>
          {title}
        </GridTitle>
      )}
      <StyledMainGrid length={features.length}>
        {features.map((feature, index) => (
          <StyledGridItem
            key={`feature-v1-griditem-${index}`}
            className={`${componentIdentifier}-gridItem`}
          >
            {feature.icon && (
              <StyledIconWrapper className={`${componentIdentifier}-icon`}>
                {feature.icon}
              </StyledIconWrapper>
            )}
            <StyledHeader>
              {feature.headingText && (
                <Typography.SH4 semibold>{feature.headingText}</Typography.SH4>
              )}
            </StyledHeader>

            {feature.paragraphText && !feature.children && !feature.richText && (
              <Typography.Body color={REBRAND_COLOR.INDIGO}>
                {feature.paragraphText}
                {feature.learnMoreUrl && (
                  <>
                    {' '}
                    <LearnMoreLink
                      noStyle
                      className={`${componentIdentifier}-learn-more-link`}
                      trackId={`${componentIdentifier}-learn-more`}
                      href={feature.learnMoreUrl}
                      trackEventData={{
                        location: feature.headingText || 'feature-grid'
                      }}
                    >
                      Learn More
                    </LearnMoreLink>
                  </>
                )}
              </Typography.Body>
            )}

            {(feature.children || feature.richText) && (
              <Typography.Body color={REBRAND_COLOR.INDIGO}>
                <RichTextWrapper>
                {renderFeatureRichText(feature, componentIdentifier)}
                </RichTextWrapper>
              </Typography.Body>
            )}
          </StyledGridItem>
        ))}
      </StyledMainGrid>
      {moreFeatures && moreFeatures.length > 0 && (
        <StyledAccordion
          isChild
          hasSmoothScroll={false}
          titleBgColor="grey-light"
          items={accordionData}
          className={`${componentIdentifier}feature-grid-v1-accordion`}
          trackEventData={{ location: 'feature-benefits' }}
        />
      )}
    </MainWrapper>
  );
};

export default FeatureGridV1;
